import { getCurrencySymbol } from "../../utils/currencyUtils";
import {
  formatCompact,
  getCount,
  getMrrAmount,
  getSummaryCurrency,
} from "./constants";
import { ZapIcon, AlertIcon, RenewIcon, TrendUpIcon } from "./icons";

/**
 * Pipeline card configuration — data-driven. Each card is exactly its Priority Accounts tab
 * (PAN-818 spec §7): `cards` from /priority-dashboard/summary/ carries the tab's row count and
 * MRR, and clicking a card opens that tab.
 */
const PIPELINE_CARD_DEFS = [
  {
    id: "risk",
    summaryKey: "high_risk",
    accentColor: "bg-red-500",
    icon: AlertIcon,
    label: "High-risk accounts",
    caption: "Need saving",
  },
  {
    id: "renewal",
    summaryKey: "renewal",
    accentColor: "bg-amber-500",
    icon: RenewIcon,
    label: "Up for renewal",
    caption: "Renewing soon",
  },
  {
    id: "expansion",
    summaryKey: "expansion",
    accentColor: "bg-emerald-500",
    icon: TrendUpIcon,
    label: "Expansion ready",
    caption: "Growth signals detected",
  },
];

const PipelineCard = ({
  card,
  summary,
  currencySymbol,
  onClick,
  revenueUnit,
}) => {
  const Icon = card.icon;
  const count = getCount(summary, card.summaryKey);
  const amount = getMrrAmount(summary, card.summaryKey);
  const value = `${currencySymbol}${formatCompact(
    revenueUnit === "ARR" ? amount * 12 : amount,
  )}`;

  return (
    <div
      className="relative cursor-pointer overflow-hidden rounded-[14px] border border-[#e8ecf0] bg-white px-6 py-[22px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <div
        className={`absolute left-0 top-0 h-full w-1 rounded-l-[14px] ${card.accentColor}`}
      />
      <div className="mb-3.5 flex items-start justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-slate-50 text-slate-500">
          <Icon />
        </div>
        <div className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-500">
          {count}
        </div>
      </div>
      <div className="mb-1 text-[12px] font-semibold uppercase tracking-[0.5px] text-slate-400">
        {card.label}
      </div>
      <div className="mb-0.5 text-[28px] font-extrabold leading-none tracking-[-0.8px] text-slate-800">
        {value}
      </div>
      <div className="text-xs font-medium text-slate-400">{card.caption}</div>
    </div>
  );
};

export const RevenuePipeline = ({
  cards,
  onFilterChange,
  revenueUnit = "MRR",
}) => {
  const sym = getCurrencySymbol(getSummaryCurrency(cards));

  return (
    <div className="mb-1.5">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-800">
        <ZapIcon />
        Revenue Pipeline by Objective
      </div>
      <div className="grid grid-cols-3 gap-4">
        {PIPELINE_CARD_DEFS.map((card) => (
          <PipelineCard
            key={card.id}
            card={card}
            summary={cards}
            currencySymbol={sym}
            onClick={() => onFilterChange?.(card.summaryKey)}
            revenueUnit={revenueUnit}
          />
        ))}
      </div>
    </div>
  );
};
